/* eslint-disable camelcase */
import { IMapyBoxItem, TActivityType } from "~/interfaces";
import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { folderCreate, likeUpdate, setFolderData, trackCopy, trackLink } from "~/util/providers/mapybox";
import { mapyboxNotification } from "~/util/mapybox";
import { MAX_TITLE_LEN } from "./const";
import ActivitySelect from "~/ui/activities/select";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";

interface IState {
	usertitle: string;
	note: string;
	trackType: TActivityType;
}

interface IMyMapsLinkActivity extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	enableNote?: boolean;
	trackData?: IMapyBoxItem;
	isForeign?: boolean;
	title?: string;
	folder?: {
		id: string;
		title: string;
	};
	onCreateFolder?: () => void;
	onSave?: (likeData: Partial<IMapyBoxItem>) => void;
	onError?: () => void;
}

const DEFAULT_TRACK_TYPE = "pesky";

export default class MyMapsLinkActivity {
	_opts: IMyMapsLinkActivity;

	_isReplace: boolean;

	_dialog: MyMapsDialog;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_noteElem: HTMLTextAreaElement;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	_activitySelect: ActivitySelect;

	constructor(data: IMyMapsLinkActivity) {
		this._opts = {
			title: "",
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onError: () => {},
			onCreateFolder: () => {},
			isForeign: false,
			enableNote: false,
			...data,
		};
		this._state = {
			usertitle: "",
			note: "",
			trackType: DEFAULT_TRACK_TYPE,
			...this._opts.trackData
				? {
					usertitle: this._opts.trackData.usertitle || this._opts.trackData.title || this._opts.title,
					note: this._opts.trackData.description || "",
					trackType: this._opts.trackData.data?.type || DEFAULT_TRACK_TYPE,
				}
				: {},
		};
		this._origState = {
			...this._state,
		};
		this._changed = false;
		this._noteElem = null;

		this._build();
	}

	open() {
		this._dialog?.open();
		!this._isReplace && this._noteElem?.focus();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("save")],
				saveBtn: _("save"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const mainContent = dialog.contents[0];

		// pouzijeme styl z jineho dialogu
		mainContent.classList.add("mymaps__createNoteDialog__mainContent");

		// titulek
		const titleInput = createMyMapsInput({
			bold: true,
			placeholder: _("mymap.addPopup.name"),
			onChange: usertitle => {
				this._updateState({ usertitle });
			},
			text: this._state.usertitle,
			count: MAX_TITLE_LEN,
		});

		mainContent.append(titleInput.container);

		// vybiratko typu
		if (Mapy.CUS.mapybox?.pathTypes) {
			this._activitySelect = new ActivitySelect({
				selected: this._state.trackType,
				onChange: trackType => {
					// pri editaci by nemelo byt mozne zadat all, ale kdyby se to nahodou stalo, tak to nezaneseme do dat a na server
					if (trackType === "all") {
						return;
					}

					this._updateState({ trackType });
				},
			});
			mainContent.append(this._activitySelect.container);
		}

		this._folderSelectCreate = new MyMapsFolderSelectCreate({
			folder: this._opts.folder,
			onChange: () => {
				this._updateState({});
			},
		});

		mainContent.append(this._folderSelectCreate.container);

		// poznamka
		if (this._opts.enableNote) {
			const noteData = createMyMapsNote({
				text: this._state.note,
				onChange: note => {
					this._updateState({ note });
				},
			});

			mainContent.append(noteData.container);
			this._noteElem = noteData.textarea;
		}

		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._dialog.setSaveBtnTitle = this._folderSelectCreate.state.createNewFolder ? _("mymap.folders.createAndSave") : _("save");

		this._changed = this._folderSelectCreate.changed
			|| this._state.note !== this._origState.note
			|| this._state.usertitle !== this._origState.usertitle
			|| this._state.trackType !== this._origState.trackType;
	}

	_checkClose() {
		return !this._changed;
	}

	_sendError() {
		this._opts.onError();
		this._closeDialog(true);
	}

	async _onSave() {
		this._dialog.error = "";
		this._dialog.loading = true;

		const folderState = this._folderSelectCreate.state;
		let folderId = folderState.folderId;
		let newLikeData: Partial<IMapyBoxItem> = null;

		// nova slozka
		if (folderState.createNewFolder) {
			if (folderState.newFolderTitle === "") {
				this._dialog.loading = false;
				this._dialog.error = _("mymap.folders.emptyFolder");

				return;
			}

			try {
				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;
				mapyboxNotification({
					msg: _("mymap.folders.createOk", { folder: folderState.newFolderTitle }),
				});
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.folders.createError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		}

		if (this._opts.isForeign) {
			// kopie tracku
			try {
				const trackCopyData = await trackCopy(this._opts.trackData.id, folderId, this._state.usertitle, this._state.note);
				const newLikeId = trackCopyData.copied.filter(item => "new_id" in item)[0].new_id;

				await likeUpdate(newLikeId, {
					// @ts-ignore
					"data.info.trackType": this._state.trackType,
				});
				newLikeData = {
					id: newLikeId,
					usertitle: this._state.usertitle,
					description: this._state.note,
				};

				mapyboxNotification({
					msg: _("mymap.addPopup.createOk"),
					folderTitle: folderState.folderTitle,
					folderId,
				});
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.folders.createError"),
					type: "error",
				});

				this._sendError();

				return;
			}
		} else {
			// link tracku
			try {
				newLikeData = await trackLink(this._opts.trackData.id, folderId, this._state.usertitle, this._state.note);
				// update
				await likeUpdate(newLikeData.id, {
					// @ts-ignore
					"data.info.trackType": this._state.trackType,
				});

				mapyboxNotification({
					msg: _("mymap.addPopup.createOkTrackLink"),
					folderTitle: folderState.folderTitle,
					folderId,
				});
			} catch (exc) {
				mapyboxNotification({
					msg: _("mymap.addPopup.tracklinkConflict"),
					type: "error",
				});

				this._sendError();

				return;
			}
		}

		setFolderData({
			id: folderId,
			title: folderState.folderTitle,
		});
		Mapy.getComponent("logger").log("my_maps_activity_to_folder", {
			id: newLikeData.id,
			type: this._opts.trackData.data?.info?.trackType,
		});

		this._opts.onSave({
			id: newLikeData.id,
			usertitle: this._state.usertitle,
			description: this._state.note,
		});
		this._closeDialog(true);
	}
}
